import { debounce, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import { FormEvent, useCallback, useMemo } from 'react';
import { FieldErrors, UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { SendEmailData, sendEmail } from '~/data/company';
import { EmployeesIndexed, useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import { OrganizationUnit } from '~/data/orgunits/types';
import { UseDialogStateOutput, useDialogState } from '~/hooks/useDialogState';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';

type EmailForm = {
  body: string;
  subject: string;
  employees?: number[];
  units?: number[];
  emails?: string;
  recipientCount: number;
};

export type Props = {
  open: boolean;
  onClose?: () => void;
};

export type UseSendEmailComponentOutput = {
  form: UseFormReturn<EmailForm>;
  allEmployees: EmployeesIndexed;
  loading: boolean;
  orgUnits: OrganizationUnit[];
  onDialogClose: (event?: object, reason?: string) => void;
  onSubmit: (e: FormEvent) => void;
  updateRecipientsCount: () => void;
  recipientsSectionState: UseDialogStateOutput;
};

const defaultValues: EmailForm = {
  subject: '',
  body: '',
  emails: '',
  units: [],
  employees: [],
  recipientCount: 0,
};

const getEmailList = (rawEmails: string | undefined) =>
  rawEmails ? rawEmails.replace(/\s/, '').split(',') : [];

const mapPostData = (data: EmailForm): SendEmailData => {
  const additionalEmails = getEmailList(data.emails).map((email: string) => {
    return { address: email };
  });

  return {
    subject: data.subject,
    body: data.body,
    to: {
      employees: data.employees || [],
      units: data.units || [],
      emails: additionalEmails,
    },
  };
};

export const getFirstErrorMessage = (
  errors: FieldErrors<EmailForm>,
): string => {
  const errorMessages = Object.values(errors)
    .map((error) => error?.message)
    .filter((v) => !!v);

  return errorMessages[0] ?? '';
};

export const useSendEmailComponent = ({
  onClose,
}: Props): UseSendEmailComponentOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { allEmployees, isLoading: employeesLoading } = useAllEmployees();

  const { orgUnits, isLoading: orgUnitsLoading } = useAllOrgUnits();

  const recipientsSectionState = useDialogState();

  const validationSchema = useMemo(() => {
    return yup.object().shape<EmailForm>({
      recipientCount: yup
        .number()
        .required()
        .test('hasRecipients', t('laOneRecipientNeeded'), (value) => {
          return typeof value === 'number' ? value > 0 : false;
        }),
      subject: yup.string().required(t('laFillSubBody')),
      body: yup.string().required(t('laFillSubBody')),
      emails: yup
        .string()
        .notRequired()
        .test('emails', t('laInvalidEmailList'), (value) => {
          if (!value) {
            return true;
          }

          return value
            .split(',')
            .every((potentialEmail) =>
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(potentialEmail),
            );
        }),
      units: yup.array<number>().notRequired(),
      employees: yup.array<number>().notRequired(),
    });
  }, [t]);

  const resolver = useYupValidationResolver(validationSchema);
  const form = useForm({
    resolver,
    defaultValues,
  });
  const { getValues, setValue, handleSubmit } = form;

  const onDialogClose = useCallback(
    (_event?: object, reason?: string) => {
      if (reason && ['backdropClick', 'escapeKeyDown'].includes(reason)) {
        return;
      }

      if (onClose) {
        onClose();
      }

      setValue('recipientCount', 0);
      recipientsSectionState.close();
    },
    [onClose, setValue, recipientsSectionState],
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit(async (data) => {
        try {
          const emailData = mapPostData(data);
          const response = await sendEmail(emailData);

          if (response) {
            onDialogClose();
            enqueueSnackbar(t('laEmailSendSuc'), {
              variant: 'success',
            });
          }
        } catch (e) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      }),
    [handleSubmit, onDialogClose, enqueueSnackbar, t],
  );

  const updateRecipientsCount = useMemo(
    () =>
      debounce(() => {
        const { units, employees, emails } = getValues();
        const emailList = getEmailList(emails);
        const empIds = Object.keys(allEmployees).filter((empId) => {
          const parsedEmpId = parseInt(empId, 10);
          return (
            units?.includes(allEmployees[parsedEmpId].fEmpUnit) ||
            employees?.includes(allEmployees[parsedEmpId].fEmpId)
          );
        });
        const newCount = uniq(empIds).length + uniq(emailList).length;

        setValue('recipientCount', newCount, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }, 600),
    [getValues, setValue, allEmployees],
  );

  return {
    form,
    orgUnits,
    allEmployees,
    loading: employeesLoading || orgUnitsLoading,
    onDialogClose,
    onSubmit,
    updateRecipientsCount,
    recipientsSectionState,
  };
};
